// src/lib/authApi.ts — standalone auth API calls (public endpoints, no api() wrapper)
import { getApiBaseUrl } from "./apiBase";
import { safeFetch, readJson } from './appErrors';

const API_URL = getApiBaseUrl();

export type AuthUser = {
  id: number;
  email: string;
  name: string | null;
  avatar_url?: string | null;
  role: "admin" | "lead" | "user";
  tenant_id: string;
  approved: number;
  blocked: number;
  mustChangePassword?: boolean;
  personnelNumber?: string | null;
  permissions?: string[];
  businessRole?: string | null;
  personId?: string | null;
  shiftId?: number | null;
};

type LoginResponse = {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
};

type RegisterResponse =
  | { accessToken: string; refreshToken: string; user: AuthUser; pendingApproval?: false }
  | { user: AuthUser; pendingApproval: true; message: string };

type RefreshResponse = {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
};

async function request<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await safeFetch(`${API_URL}${path}`, {
    credentials: "include",
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
    },
  });
  return readJson<T>(res,path,opts.method);
}

export function loginApi(email: string, password: string): Promise<LoginResponse> {
  return request<LoginResponse>("/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export function registerApi(
  email: string,
  password: string,
  name: string,
  setupToken?: string
): Promise<RegisterResponse> {
  return request<RegisterResponse>("/v1/auth/register", {
    method: "POST",
    body: JSON.stringify({ email, password, name, setupToken: setupToken || undefined }),
  });
}

export function refreshTokenApi(refreshToken: string): Promise<RefreshResponse> {
  return request<RefreshResponse>("/v1/auth/refresh", {
    method: "POST",
    body: JSON.stringify({ refreshToken }),
  });
}

export function logoutApi(accessToken: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>("/v1/auth/logout", {
    method: "POST",
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export function getMeApi(accessToken: string): Promise<{ user: AuthUser }> {
  return request<{ user: AuthUser }>("/v1/auth/me", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}
